import type { Socket } from 'socket.io-client';
import type { WebsiteGenerationCompletedData as WebsiteGenerationCompletedSocketData } from '@/services/website/socket/types';
import { useEffect, useRef, useState } from 'react';
import intl from 'react-intl-universal';
import { matchPath } from 'react-router-dom';
import CustomRouter from '@/modules/CustomRouter';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import NotificationSocket from '@/services/socket/NotificationSocket';
import { WEBSITE_EVENTS } from '@/services/website/socket/const';
import { useUserStore } from '@/stores/user';
import {
  SYSTEM_NOTIFICATION_PREFERENCE_CHANGED_EVENT,
  WEGIC_NOTIFICATION_ICON_URL,
} from './const';
import {
  buildInitialGenerationNotificationTag,
  claimAnonymousInitialGenerationProject,
  getInitialGenerationNotificationDecision,
  getSystemNotificationPermission,
  isSystemNotificationPreferenceEnabled,
  markInitialGenerationProjectHandled,
  readArmedInitialGenerationProjectIds,
  readHandledInitialGenerationProjectIds,
  showSystemNotification,
} from './helper';

const INITIAL_GENERATION_NOTIFICATION_ROOM_OWNER = 'initial-generation-system-notification';

function getViewedProjectId() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const pathname = window.location.pathname;
  return (
    matchPath('/workspace/projects/:projectId', pathname)?.params.projectId
    ?? matchPath('/projects/:projectId', pathname)?.params.projectId
  );
}

function openCompletedProject(projectId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.focus();
  if (getViewedProjectId() !== projectId) {
    CustomRouter.navigate(`/workspace/projects/${projectId}`);
  }
}

export function useInitialWebsiteGenerationSystemNotification() {
  const userId = useUserStore(state => state.userInfo?.userId);
  const runtimeShownKeysRef = useRef<Set<string>>(new Set());
  const [, setPreferenceRevision] = useState(0);
  const notificationEnabled = isSystemNotificationPreferenceEnabled(userId);

  useEffect(() => {
    const refreshPreference = () => setPreferenceRevision(current => current + 1);
    window.addEventListener(SYSTEM_NOTIFICATION_PREFERENCE_CHANGED_EVENT, refreshPreference);
    return () => {
      window.removeEventListener(SYSTEM_NOTIFICATION_PREFERENCE_CHANGED_EVENT, refreshPreference);
    };
  }, []);

  useEffect(() => {
    if (!notificationEnabled || !userId) {
      return;
    }

    const notificationOwnerId = userId;
    const joinedProjectIds = new Set<string>();
    let subscribedSocket: Socket | undefined;

    const viewedProjectId = getViewedProjectId();
    if (viewedProjectId) {
      claimAnonymousInitialGenerationProject(viewedProjectId, notificationOwnerId);
    }
    runtimeShownKeysRef.current.clear();
    NotificationSocket.initialize(notificationOwnerId);

    const joinProjectRoom = (projectId: string) => {
      if (joinedProjectIds.has(projectId)) {
        return;
      }
      joinedProjectIds.add(projectId);
      NotificationSocket.joinRoom(projectId, INITIAL_GENERATION_NOTIFICATION_ROOM_OWNER);
    };

    const completeProject = (projectId: string) => {
      markInitialGenerationProjectHandled(projectId, notificationOwnerId);
      joinedProjectIds.delete(projectId);
      NotificationSocket.leaveRoom(projectId, INITIAL_GENERATION_NOTIFICATION_ROOM_OWNER);
    };

    const handleGenerationCompleted = (payload: WebsiteGenerationCompletedSocketData['data']) => {
      if (!payload?.projectId || !payload.messageId) {
        return;
      }

      const armedProjectIds = readArmedInitialGenerationProjectIds(notificationOwnerId);
      if (!armedProjectIds.includes(payload.projectId)) {
        return;
      }

      const decision = getInitialGenerationNotificationDecision({
        payload,
        armedProjectIds,
        runtimeShownKeys: runtimeShownKeysRef.current,
        handledProjectIds: readHandledInitialGenerationProjectIds(notificationOwnerId),
        notificationPermission: getSystemNotificationPermission(),
        visibilityState: typeof document === 'undefined' ? 'visible' : document.visibilityState,
      });

      if (decision.shouldShow && decision.eventKey && decision.projectId) {
        runtimeShownKeysRef.current.add(decision.eventKey);
        showSystemNotification({
          title: intl.get('systemNotification.initialGenerationCompleted.title'),
          body: intl.get('systemNotification.initialGenerationCompleted.body'),
          icon: WEGIC_NOTIFICATION_ICON_URL,
          tag: buildInitialGenerationNotificationTag(decision.projectId),
          onClick: () => openCompletedProject(decision.projectId!),
        });
      }

      completeProject(payload.projectId);
    };

    const handleSocketGenerationCompleted = (payload: WebsiteGenerationCompletedSocketData) => {
      handleGenerationCompleted(payload.data);
    };

    const attachSocketListener = (socket: Socket) => {
      if (subscribedSocket === socket) {
        return;
      }
      subscribedSocket?.off(WEBSITE_EVENTS.GENERATION_COMPLETED, handleSocketGenerationCompleted);
      subscribedSocket = socket;
      socket.on(WEBSITE_EVENTS.GENERATION_COMPLETED, handleSocketGenerationCompleted);
      readArmedInitialGenerationProjectIds(notificationOwnerId).forEach(joinProjectRoom);
    };

    const handleNotificationArmed = ({ projectId }: { projectId: string }) => {
      joinProjectRoom(projectId);
    };

    globalEventEmitter.on(EVENTS.INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED, handleNotificationArmed);
    const unsubscribeSocketAvailable = NotificationSocket.onSocketAvailable(attachSocketListener);
    readArmedInitialGenerationProjectIds(notificationOwnerId).forEach(joinProjectRoom);

    return () => {
      globalEventEmitter.off(EVENTS.INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED, handleNotificationArmed);
      unsubscribeSocketAvailable();
      subscribedSocket?.off(WEBSITE_EVENTS.GENERATION_COMPLETED, handleSocketGenerationCompleted);
      joinedProjectIds.forEach(projectId => {
        NotificationSocket.leaveRoom(projectId, INITIAL_GENERATION_NOTIFICATION_ROOM_OWNER);
      });
    };
  }, [notificationEnabled, userId]);
}
